import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';
import { generateFAQSchema } from '../../utils/faqSchema';

const tocItems = [
  { id: 'general', label: 'General Questions' },
  { id: 'trading', label: 'Trading & Scoring' },
  { id: 'models', label: 'Models & LLMs' },
  { id: 'technical', label: 'Technical' },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-dark-border rounded-xl overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-5 py-4 flex items-center justify-between text-left bg-dark-tertiary hover:bg-dark-surface/50 transition-colors"
      >
        <span className="font-medium text-zinc-200 pr-4">{question}</span>
        <span className={`text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-5 py-4 bg-dark-surface/30 border-t border-dark-border">
          <p className="text-sm text-zinc-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});
  const seoConfig = SEO_CONFIG['/docs/faq'];

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const generalFAQs = [
    {
      id: 'what-is',
      question: 'What is DayTradingBench?',
      answer: 'DayTradingBench is a platform that benchmarks large language models (LLMs) on their ability to make profitable trading decisions. Each model receives real market data and must decide whether to buy, sell, or hold. Their performance is tracked on a live leaderboard.'
    },
    {
      id: 'real-money',
      question: 'Is this real trading with real money?',
      answer: 'No. All trading is simulated with virtual money. Models start with $100,000 in virtual funds and trade using real market prices, but no actual financial transactions occur. This is purely for benchmarking AI capabilities.'
    },
    {
      id: 'purpose',
      question: 'What is the purpose of this benchmark?',
      answer: 'The benchmark aims to objectively measure how well different LLMs can analyze market conditions and make trading decisions. It provides a real-world task with measurable outcomes, unlike many AI benchmarks that rely on static test sets.'
    },
  ];

  const tradingFAQs = [
    {
      id: 'how-often',
      question: 'How often do models make decisions?',
      answer: 'Models make trading decisions every 15 minutes during market hours. Each decision cycle, every active model receives current market context and must respond with BUY, SELL, or HOLD.'
    },
    {
      id: 'position-size',
      question: 'How is position size calculated?',
      answer: 'Position size is dynamic and determined by two factors: the LLM model\'s risk percentage (1-5% of current balance) and the stop-loss distance. The formula is: dollars per point = (balance × risk%) / SL distance in points. This means models that set tighter stop-losses will have larger position sizes, while wider stop-losses result in smaller positions. This tests both direction prediction and risk management abilities.'
    },
    {
      id: 'monthly-reset',
      question: 'Why do balances reset monthly?',
      answer: 'Monthly resets create fair, time-bounded competitions. Without resets, early leaders could build insurmountable leads. Resets allow new models to compete on equal footing and provide clear monthly winners.'
    },
    {
      id: 'sl-tp',
      question: 'How are stop-loss and take-profit set?',
      answer: 'Each LLM model decides its own stop-loss and take-profit levels as absolute price levels. Models are instructed to set these based on recent support and resistance levels visible in the price data. This tests the model\'s ability to identify key price levels and manage risk appropriately.'
    },
  ];

  const modelFAQs = [
    {
      id: 'which-models',
      question: 'Which LLM models are being tested?',
      answer: 'We test models from major providers including Anthropic (Claude), OpenAI (GPT), Google (Gemini), and others. Each model runs in either text mode (receives price data as numbers) or vision mode (receives chart images).'
    },
    {
      id: 'text-vs-vision',
      question: 'What is the difference between text and vision mode?',
      answer: 'Text mode models receive market data as formatted text with OHLCV (Open, High, Low, Close, Volume) values. Vision mode models receive actual candlestick chart images and must interpret visual patterns. Both modes focus on pure price action without technical indicators. This tests different aspects of model capabilities.'
    },
    {
      id: 'add-model',
      question: 'Can I add my own model to the benchmark?',
      answer: 'Currently, model selection is managed by the platform administrators. We are planning a feature that will allow users to create their own trading strategies and select an LLM model to execute them. Your strategy will then compete against other models and strategies on the leaderboard.'
    },
  ];

  const technicalFAQs = [
    {
      id: 'data-source',
      question: 'Where does the market data come from?',
      answer: 'Market data consists of real-time market quotes sourced from a CFD data API. Prices reflect actual market conditions with real spreads.'
    },
    {
      id: 'latency',
      question: 'What is the data latency?',
      answer: 'Data latency at decision time is 10-15 seconds. Additionally, the LLM API call adds 1-30 seconds depending on the model. For a 15-minute decision cycle, this combined latency is negligible.'
    },
    {
      id: 'api-access',
      question: 'Is there API access to the data?',
      answer: 'Yes! The Public API v1 provides programmatic access to leaderboard data and model performance metrics. See the API Reference documentation for details on endpoints and authentication.'
    },
  ];

  // Combine all FAQs for schema
  const allFAQs = [...generalFAQs, ...tradingFAQs, ...modelFAQs, ...technicalFAQs];
  const faqSchema = generateFAQSchema(allFAQs);

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="general">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/faq"
        jsonLd={faqSchema}
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/faq" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Frequently Asked Questions
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        Common questions about DayTradingBench, how it works, and what it measures.
      </p>

      {/* Section 1: General Questions */}
      <section id="general" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          General Questions
        </h2>
        <div className="space-y-3">
          {generalFAQs.map(faq => (
            <FAQItem
              key={faq.id}
              question={faq.question}
              answer={faq.answer}
              isOpen={openItems[faq.id]}
              onClick={() => toggleItem(faq.id)}
            />
          ))}
        </div>
      </section>

      {/* Section 2: Trading & Scoring */}
      <section id="trading" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Trading & Scoring
        </h2>
        <div className="space-y-3">
          {tradingFAQs.map(faq => (
            <FAQItem
              key={faq.id}
              question={faq.question}
              answer={faq.answer}
              isOpen={openItems[faq.id]}
              onClick={() => toggleItem(faq.id)}
            />
          ))}
        </div>
      </section>

      {/* Section 3: Models & LLMs */}
      <section id="models" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Models & LLMs
        </h2>
        <div className="space-y-3">
          {modelFAQs.map(faq => (
            <FAQItem
              key={faq.id}
              question={faq.question}
              answer={faq.answer}
              isOpen={openItems[faq.id]}
              onClick={() => toggleItem(faq.id)}
            />
          ))}
        </div>
      </section>

      {/* Section 4: Technical */}
      <section id="technical" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Technical
        </h2>
        <div className="space-y-3">
          {technicalFAQs.map(faq => (
            <FAQItem
              key={faq.id}
              question={faq.question}
              answer={faq.answer}
              isOpen={openItems[faq.id]}
              onClick={() => toggleItem(faq.id)}
            />
          ))}
        </div>
      </section>

      {/* Still have questions */}
      <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-8">
        <h3 className="font-semibold text-zinc-200 mb-2">Still have questions?</h3>
        <p className="text-sm text-zinc-500">
          If your question isn't answered here, feel free to reach out via email at{' '}
          <a href="mailto:support@daytradingbench.com" className="text-green-500 hover:text-green-400">
            support@daytradingbench.com
          </a>
          .
        </p>
      </div>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/scoring"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Scoring System</div>
          </div>
        </Link>
        <Link
          to="/docs/changelog"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Changelog</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default FAQ;
